<template>
  <div class="page">

    <div class="page-header">
      <div>
        <button
          class="back-btn"
          @click="$router.push('/courses')"
        >
          <i class="bx bx-arrow-back"></i>
          Courses
        </button>

        <h2>
          {{ course.title || 'Course details' }}
        </h2>

        <p>
          #{{ course.course_id }}
          · {{ course.category_name || 'Course' }}
          · {{ course.creator_name || '-' }}
        </p>
      </div>

      <button
        class="btn-secondary"
        @click="loadDetails"
      >
        <i class="bx bx-refresh"></i>
        Refresh
      </button>
    </div>

    <div
      v-if="error"
      class="error-box"
    >
      {{ error }}
    </div>

    <div
      v-if="loading"
      class="card loading-box"
    >
      Loading course details...
    </div>

    <template v-else>

      <!-- SUMMARY -->
      <div class="summary-grid">

        <div class="summary-card">
          <span>Orders</span>
          <strong>
            {{ course.report?.totalOrders || 0 }}
          </strong>
        </div>

        <div class="summary-card">
          <span>Learners with progress</span>
          <strong>
            {{ course.report?.totalLearners || 0 }}
          </strong>
        </div>

        <div class="summary-card">
          <span>Lectures</span>
          <strong>
            {{ course.report?.totalLectures || 0 }}
          </strong>
        </div>

        <div class="summary-card">
          <span>Videos</span>
          <strong>
            {{ course.videos?.length || 0 }}
          </strong>
        </div>

      </div>


      <!-- TABS -->
      <div class="tabs">

        <button
          :class="{ active: tab === 'report' }"
          @click="tab = 'report'"
        >
          Orders & progress
        </button>

        <button
          :class="{ active: tab === 'curriculum' }"
          @click="tab = 'curriculum'"
        >
          Modules & lectures
        </button>

        <button
          :class="{ active: tab === 'videos' }"
          @click="tab = 'videos'"
        >
          Course videos
        </button>

      </div>


      <!-- =====================================================
           REPORT
      ====================================================== -->

      <section
        v-if="tab === 'report'"
        class="report-content"
      >

        <!--
          ROW 1
          70% Orders BAR
          30% Category Sales DONUT
        -->

        <div class="chart-row">

          <div class="card chart-card main-chart-card">

            <div class="section-header">
              <h3>Orders by date</h3>
            </div>

            <div
              id="courseOrdersChart"
              class="main-chart"
            ></div>

          </div>


          <div class="card chart-card donut-chart-card">

            <div class="section-header">
              <h3>Category sales</h3>
            </div>

            <div
              id="courseCategorySalesChart"
              class="donut-chart"
            ></div>

          </div>

        </div>


        <!--
          ROW 2
          70% Views LINE
          30% Category Views DONUT
        -->

        <div class="chart-row">

          <div class="card chart-card main-chart-card">

            <div class="section-header">
              <h3>Views by date</h3>
            </div>

            <div
              id="courseViewsChart"
              class="main-chart"
            ></div>

          </div>


          <div class="card chart-card donut-chart-card">

            <div class="section-header">
              <h3>Category views</h3>
            </div>

            <div
              id="courseCategoryViewsChart"
              class="donut-chart"
            ></div>

          </div>

        </div>


        <!-- ORDERS TABLE -->
        <div class="content-grid">

          <div class="card table-card">

            <div class="section-header">
              <h3>Orders</h3>
            </div>

            <div class="table-wrapper">

              <table class="modern-table">

                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Learner</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>

                <tbody>

                  <tr
                    v-for="order in course.orders"
                    :key="order.order_id"
                  >
                    <td>
                      #{{ order.order_ref }}
                    </td>

                    <td>
                      {{ order.user_name }}
                      <small>
                        {{ order.user_email }}
                      </small>
                    </td>

                    <td>
                      ₹{{ order.amount || 0 }}
                    </td>

                    <td>
                      <span class="badge">
                        {{ order.order_status }}
                      </span>
                    </td>

                    <td>
                      {{ formatDate(order.created_at) }}
                    </td>
                  </tr>

                  <tr
                    v-if="!course.orders?.length"
                  >
                    <td
                      colspan="5"
                      class="empty"
                    >
                      No orders found.
                    </td>
                  </tr>

                </tbody>

              </table>

            </div>

          </div>


          <!-- WATCH PROGRESS -->

          <div class="card table-card">

            <div class="section-header">
              <h3>Watch progress</h3>
            </div>

            <div class="table-wrapper">

              <table class="modern-table">

                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Completed</th>
                    <th>Progress</th>
                    <th>Last activity</th>
                  </tr>
                </thead>

                <tbody>

                  <tr
                    v-for="item in course.progress"
                    :key="item.user_id"
                  >

                    <td>
                      {{ item.user_name }}

                      <small>
                        {{ item.user_email }}
                      </small>
                    </td>

                    <td>
                      {{ item.completed_lectures }}
                      /
                      {{ course.report?.totalLectures || 0 }}
                    </td>

                    <td>
                      <strong>
                        {{ item.progress }}%
                      </strong>
                    </td>

                    <td>
                      {{ formatDate(item.last_activity) }}
                    </td>

                  </tr>

                  <tr
                    v-if="!course.progress?.length"
                  >
                    <td
                      colspan="4"
                      class="empty"
                    >
                      No watch progress found.
                    </td>
                  </tr>

                </tbody>

              </table>

            </div>

          </div>

        </div>

      </section>


      <!-- =====================================================
           CURRICULUM
      ====================================================== -->

      <section
        v-if="tab === 'curriculum'"
        class="card section-card"
      >

        <div class="section-header">

          <h3>
            Modules and lectures
          </h3>

          <button
            class="btn-primary"
            @click="openModule()"
          >
            + Add module
          </button>

        </div>


        <div
          v-for="module in course.modules"
          :key="module.module_id"
          class="module-block"
        >

          <div class="module-header">

            <div>

              <strong>
                {{ module.module_title }}
              </strong>

              <small>
                {{ module.module_desc || 'No description' }}
              </small>

            </div>

            <div>

              <button
                class="icon-btn edit"
                @click="openModule(module)"
              >
                <i class="bx bx-edit"></i>
              </button>

              <button
                class="icon-btn delete"
                @click="removeModule(module)"
              >
                <i class="bx bx-trash"></i>
              </button>

            </div>

          </div>


          <div
            v-for="lecture in module.lectures"
            :key="lecture.lession_id"
            class="lecture-row"
          >

            <span>
              <i class="bx bx-play-circle"></i>
              {{ lecture.lession_title }}
            </span>

            <small>
              {{ lecture.lession_duration || 0 }}
              min
            </small>

            <div>

              <button
                class="icon-btn edit"
                @click="
                  openLecture(
                    lecture,
                    module.module_id
                  )
                "
              >
                <i class="bx bx-edit"></i>
              </button>

              <button
                class="icon-btn delete"
                @click="removeLecture(lecture)"
              >
                <i class="bx bx-trash"></i>
              </button>

            </div>

          </div>


          <button
            class="link-btn"
            @click="
              openLecture(
                null,
                module.module_id
              )
            "
          >
            + Add lecture
          </button>

        </div>


        <div
          v-if="!course.modules?.length"
          class="empty"
        >
          No modules found.
        </div>

      </section>


      <!-- =====================================================
           VIDEOS
      ====================================================== -->

      <section
        v-if="tab === 'videos'"
        class="card section-card"
      >

        <div class="section-header">

          <h3>
            Video entries
          </h3>

          <button
            class="btn-primary"
            @click="openVideo()"
          >
            + Add video
          </button>

        </div>


        <div class="table-wrapper">

          <table class="modern-table">

            <thead>
              <tr>
                <th>Title</th>
                <th>URL</th>
                <th>Order</th>
                <th></th>
              </tr>
            </thead>

            <tbody>

              <tr
                v-for="video in course.videos"
                :key="video.course_video_id"
              >

                <td>
                  {{ video.video_title }}
                </td>

                <td class="url-cell">
                  {{ video.video_url }}
                </td>

                <td>
                  {{ video.sort_order }}
                </td>

                <td class="actions">

                  <button
                    class="icon-btn edit"
                    @click="openVideo(video)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    @click="removeVideo(video)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </td>

              </tr>


              <tr
                v-if="!course.videos?.length"
              >
                <td
                  colspan="4"
                  class="empty"
                >
                  No course videos found.
                </td>
              </tr>

            </tbody>

          </table>

        </div>

      </section>

    </template>


    <!-- =====================================================
         ADD / EDIT MODAL
    ====================================================== -->

    <div
      v-if="formType"
      class="modal-overlay"
      @click.self="formType = ''"
    >

      <div class="modal-box">

        <div class="section-header">

          <h3>
            {{ editingId ? 'Edit' : 'Add' }}
            {{ formType }}
          </h3>

          <button
            class="modal-close"
            @click="formType = ''"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>


        <div class="form-grid">

          <!-- MODULE -->

          <template
            v-if="formType === 'module'"
          >

            <label>
              Title

              <input
                v-model="form.module_title"
              />

            </label>


            <label>
              Type

              <input
                v-model="form.module_type"
                placeholder="module"
              />

            </label>


            <label class="full">
              Description

              <textarea
                v-model="form.module_desc"
              ></textarea>

            </label>

          </template>


          <!-- LECTURE -->

          <template
            v-if="formType === 'lecture'"
          >

            <label>
              Title

              <input
                v-model="form.lession_title"
              />

            </label>


            <label>
              Duration (minutes)

              <input
                v-model.number="form.lession_duration"
                type="number"
              />

            </label>


            <label>
              Video URL

              <input
                v-model="form.lession_video"
              />

            </label>


            <label class="full">
              Description

              <textarea
                v-model="form.lession_desc"
              ></textarea>

            </label>

          </template>


          <!-- VIDEO -->

          <template
            v-if="formType === 'video'"
          >

            <label>
              Title

              <input
                v-model="form.video_title"
              />

            </label>


            <label>
              Video URL

              <input
                v-model="form.video_url"
              />

            </label>


            <label>
              Sort order

              <input
                v-model.number="form.sort_order"
                type="number"
              />

            </label>


            <label class="full">
              Description

              <textarea
                v-model="form.video_desc"
              ></textarea>

            </label>

          </template>

        </div>


        <div class="modal-actions">

          <button
            class="btn-secondary"
            @click="formType = ''"
          >
            Cancel
          </button>

          <button
            class="btn-primary"
            @click="saveForm"
          >
            Save
          </button>

        </div>

      </div>

    </div>


    <!-- =====================================================
         DELETE CONFIRMATION
    ====================================================== -->

    <div
      v-if="confirmDialog.open"
      class="modal-overlay"
      @click.self="closeConfirm"
    >

      <div class="modal-box confirm-box">

        <div class="confirm-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h3>
          Delete {{ confirmDialog.type }}?
        </h3>

        <p>
          This will permanently remove
          <strong>
            {{ confirmDialog.label }}
          </strong>.
        </p>

        <div class="modal-actions">

          <button
            class="btn-secondary"
            @click="closeConfirm"
          >
            Cancel
          </button>

          <button
            class="btn-danger"
            @click="confirmDelete"
          >
            Delete
          </button>

        </div>

      </div>

    </div>

  </div>
</template>


<script>
import ApexCharts from "apexcharts";

import {
    getCourseDetailApi,
    createCourseModuleApi,
    updateCourseModuleApi,
    deleteCourseModuleApi,
    createCourseLectureApi,
    updateCourseLectureApi,
    deleteCourseLectureApi,
    createCourseVideoApi,
    updateCourseVideoApi,
    deleteCourseVideoApi
} from "@/apis/coursedetailapis";


export default {

    name: "CourseDetailsScreen",


    data() {
        return {

            course: {},

            tab: "report",

            loading: false,

            error: "",

            formType: "",

            editingId: 0,

            form: {},

            charts: {},

            confirmDialog: {
                open: false,
                type: "",
                label: "",
                id: 0
            }

        };
    },


    mounted() {
        this.loadDetails();
    },


    beforeUnmount() {
        this.destroyCharts();
    },


    watch: {

        tab(value) {

            this.destroyCharts();

            if (value === "report") {

                this.$nextTick(() => {

                    requestAnimationFrame(() => {
                        this.initCharts();
                    });

                });

            }

        }

    },


    methods: {

        /* =====================================================
           LOAD COURSE
        ===================================================== */

        async loadDetails() {

            this.loading = true;

            this.error = "";

            try {

                const response =
                    await getCourseDetailApi(
                        this.$route.params.courseId
                    );


                if (response?.status) {

                    this.course =
                        response.course || {};

                } else {

                    this.error =
                        response?.success ||
                        "Unable to load course details.";

                }


                this.loading = false;


                if (
                    response?.status &&
                    this.tab === "report"
                ) {

                    await this.$nextTick();

                    requestAnimationFrame(() => {
                        this.initCharts();
                    });

                }

            } catch (error) {

                console.error(
                    "Course details error:",
                    error
                );

                this.error =
                    "Unable to load course details.";

                this.loading = false;

            }

        },


        /* =====================================================
           DESTROY CHARTS
        ===================================================== */

        destroyCharts() {

            Object.keys(this.charts).forEach(
                (key) => {

                    const chart =
                        this.charts[key];

                    if (chart) {

                        try {
                            chart.destroy();
                        } catch (error) {
                            console.warn(
                                `Chart destroy error (${key}):`,
                                error
                            );
                        }

                    }

                }
            );


            this.charts = {};

        },


        /* =====================================================
           INIT ALL CHARTS
        ===================================================== */

        initCharts() {

            if (this.tab !== "report") {
                return;
            }


            this.destroyCharts();


            this.$nextTick(() => {

                this.initOrdersChart();

                this.initViewsChart();

                this.initCategoryChart();

            });

        },


        /* =====================================================
           NUMBER FORMAT
        ===================================================== */

        formatNumber(value) {

            const number =
                Number(value || 0);


            if (!Number.isFinite(number)) {
                return "0";
            }


            if (number >= 10000000) {

                return (
                    (number / 10000000)
                        .toFixed(1)
                        .replace(/\.0$/, "") +
                    "Cr"
                );

            }


            if (number >= 100000) {

                return (
                    (number / 100000)
                        .toFixed(1)
                        .replace(/\.0$/, "") +
                    "L"
                );

            }


            if (number >= 1000) {

                return (
                    (number / 1000)
                        .toFixed(1)
                        .replace(/\.0$/, "") +
                    "K"
                );

            }


            return number.toLocaleString("en-IN");

        },


        /* =====================================================
           CHART DATE FORMAT
        ===================================================== */

        formatChartDate(value) {

            if (!value) {
                return "Unknown";
            }


            const date =
                new Date(value);


            if (
                Number.isNaN(
                    date.getTime()
                )
            ) {

                return String(value);

            }


            return date.toLocaleDateString(
                "en-IN",
                {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                }
            );

        },


        /* =====================================================
           BAR + LINE OPTIONS
           
           IMPORTANT:
           No undefined ApexCharts properties.
        ===================================================== */

        getCartesianOptions(
            type,
            categories,
            series,
            yAxisTitle
        ) {

            const options = {

                chart: {

                    type: type,

                    height: 300,

                    toolbar: {
                        show: false
                    },

                    fontFamily:
                        "inherit",

                    animations: {
                        enabled: true
                    }

                },


                series: series,


                colors: [
                    "#2563eb"
                ],


                xaxis: {

                    categories:
                        categories,

                    labels: {

                        rotate:
                            categories.length > 8
                                ? -45
                                : 0,

                        rotateAlways:
                            categories.length > 8,

                        hideOverlappingLabels:
                            true,

                        trim: true,

                        style: {

                            colors:
                                "#64748b",

                            fontSize:
                                "10px"

                        }

                    },

                    axisBorder: {

                        show: true,

                        color:
                            "#e2e8f0"

                    },

                    axisTicks: {

                        show: true,

                        color:
                            "#e2e8f0"

                    }

                },


                yaxis: {

                    labels: {

                        formatter: (value) => {

                            return this.formatNumber(
                                value
                            );

                        },

                        style: {

                            colors:
                                "#64748b",

                            fontSize:
                                "11px"

                        }

                    }

                },


                grid: {

                    show: true,

                    borderColor:
                        "#e2e8f0",

                    strokeDashArray:
                        4

                },


                tooltip: {

                    enabled: true,

                    theme: "light",

                    y: {

                        formatter: (value) => {

                            return this.formatNumber(
                                value
                            );

                        }

                    }

                },


                legend: {

                    show: true,

                    position:
                        "bottom",

                    fontSize:
                        "11px"

                },


                noData: {

                    text:
                        "No data available",

                    align:
                        "center",

                    verticalAlign:
                        "middle",

                    style: {

                        color:
                            "#64748b",

                        fontSize:
                            "12px"

                    }

                }

            };


            /* BAR */

            if (type === "bar") {

                options.plotOptions = {

                    bar: {

                        horizontal:
                            false,

                        borderRadius:
                            5,

                        columnWidth:
                            "48%"

                    }

                };


                options.stroke = {

                    width: 0

                };

            }


            /* LINE */

            if (type === "line") {

                options.stroke = {

                    width: 3,

                    curve:
                        "smooth"

                };


                options.markers = {

                    size: 4,

                    strokeWidth:
                        2,

                    hover: {

                        size: 6

                    }

                };

            }


            /* Y AXIS TITLE */

            if (yAxisTitle) {

                options.yaxis.title = {

                    text:
                        yAxisTitle,

                    style: {

                        color:
                            "#64748b",

                        fontSize:
                            "11px",

                        fontWeight:
                            500

                    }

                };

            }


            return options;

        },


        /* =====================================================
           DONUT OPTIONS
        ===================================================== */

        getDonutOptions(
            labels,
            values,
            centerLabel
        ) {

            return {

                chart: {

                    type:
                        "donut",

                    height:
                        270,

                    toolbar: {

                        show:
                            false

                    },

                    fontFamily:
                        "inherit",

                    animations: {

                        enabled:
                            true

                    }

                },


                series:
                    values,


                labels:
                    labels,


                colors: [

                    "#2563eb",

                    "#f59e0b",

                    "#10b981",

                    "#ef4444",

                    "#8b5cf6",

                    "#06b6d4",

                    "#ec4899",

                    "#14b8a6"

                ],


                stroke: {

                    width:
                        2,

                    colors: [
                        "#ffffff"
                    ]

                },


                plotOptions: {

                    pie: {

                        expandOnClick:
                            true,

                        donut: {

                            size:
                                "68%",

                            labels: {

                                show:
                                    true,


                                name: {

                                    show:
                                        true,

                                    fontSize:
                                        "11px",

                                    color:
                                        "#64748b"

                                },


                                value: {

                                    show:
                                        true,

                                    fontSize:
                                        "17px",

                                    fontWeight:
                                        700,

                                    color:
                                        "#0f172a",

                                    formatter:
                                        (value) => {

                                            return this.formatNumber(
                                                value
                                            );

                                        }

                                },


                                total: {

                                    show:
                                        true,

                                    showAlways:
                                        true,

                                    label:
                                        centerLabel,

                                    color:
                                        "#64748b",

                                    fontSize:
                                        "11px",

                                    formatter:
                                        (w) => {

                                            const total =
                                                w.globals.seriesTotals.reduce(
                                                    (
                                                        sum,
                                                        value
                                                    ) =>
                                                        sum +
                                                        Number(
                                                            value ||
                                                            0
                                                        ),
                                                    0
                                                );

                                            return this.formatNumber(
                                                total
                                            );

                                        }

                                }

                            }

                        }

                    }

                },


                dataLabels: {

                    enabled:
                        true,

                    formatter:
                        (value) => {

                            return (
                                Number(value)
                                    .toFixed(1) +
                                "%"
                            );

                        },

                    style: {

                        fontSize:
                            "10px",

                        fontWeight:
                            600

                    }

                },


                legend: {

                    show:
                        true,

                    position:
                        "bottom",

                    fontSize:
                        "10px"

                },


                tooltip: {

                    enabled:
                        true,

                    theme:
                        "light",

                    y: {

                        formatter:
                            (value) => {

                                return this.formatNumber(
                                    value
                                );

                            }

                    }

                },


                noData: {

                    text:
                        "No data available",

                    align:
                        "center",

                    verticalAlign:
                        "middle",

                    style: {

                        color:
                            "#64748b",

                        fontSize:
                            "12px"

                    }

                }

            };

        },


        /* =====================================================
           ORDERS BAR
        ===================================================== */

        initOrdersChart() {

            const element =
                document.querySelector(
                    "#courseOrdersChart"
                );


            if (!element) {
                return;
            }


            const report =
                this.course?.report || {};


            const rows =
                Array.isArray(
                    report.orderSeries
                )
                    ? report.orderSeries
                    : [];


            const categories =
                rows.length
                    ? rows.map(
                        (item) =>
                            this.formatChartDate(
                                item.period
                            )
                    )
                    : ["No data"];


            const values =
                rows.length
                    ? rows.map(
                        (item) =>
                            Number(
                                item.total || 0
                            )
                    )
                    : [0];


            const options =
                this.getCartesianOptions(
                    "bar",
                    categories,
                    [
                        {
                            name:
                                "Orders",

                            data:
                                values
                        }
                    ],
                    "Orders"
                );


            try {

                const chart =
                    new ApexCharts(
                        element,
                        options
                    );


                chart.render();


                this.charts.orders =
                    chart;

            } catch (error) {

                console.error(
                    "Orders chart error:",
                    error
                );

            }

        },


        /* =====================================================
           VIEWS LINE
        ===================================================== */

        initViewsChart() {

            const element =
                document.querySelector(
                    "#courseViewsChart"
                );


            if (!element) {
                return;
            }


            const report =
                this.course?.report || {};


            const rows =
                Array.isArray(
                    report.viewSeries
                )
                    ? report.viewSeries
                    : [];


            const categories =
                rows.length
                    ? rows.map(
                        (item) =>
                            this.formatChartDate(
                                item.period
                            )
                    )
                    : ["No data"];


            const values =
                rows.length
                    ? rows.map(
                        (item) =>
                            Number(
                                item.total || 0
                            )
                    )
                    : [0];


            const options =
                this.getCartesianOptions(
                    "line",
                    categories,
                    [
                        {
                            name:
                                "Views",

                            data:
                                values
                        }
                    ],
                    "Views"
                );


            try {

                const chart =
                    new ApexCharts(
                        element,
                        options
                    );


                chart.render();


                this.charts.views =
                    chart;

            } catch (error) {

                console.error(
                    "Views chart error:",
                    error
                );

            }

        },


        /* =====================================================
           CATEGORY SALES + CATEGORY VIEWS
           
           They are completely independent donuts.
        ===================================================== */

        initCategoryChart() {

            const salesElement =
                document.querySelector(
                    "#courseCategorySalesChart"
                );


            const viewsElement =
                document.querySelector(
                    "#courseCategoryViewsChart"
                );


            if (
                !salesElement ||
                !viewsElement
            ) {
                return;
            }


            const rows =
                Array.isArray(
                    this.course?.report
                        ?.categorySeries
                )
                    ? this.course.report
                        .categorySeries
                    : [];


            /* =================================================
               SALES
            ================================================= */

            const salesRows =
                rows
                    .map(
                        (item) => ({

                            category:
                                item.category ||
                                "Uncategorised",

                            value:
                                Number(
                                    item.sales ||
                                    0
                                )

                        })
                    )
                    .filter(
                        (item) =>

                            Number.isFinite(
                                item.value
                            ) &&

                            item.value > 0
                    );


            const salesLabels =
                salesRows.length
                    ? salesRows.map(
                        (item) =>
                            item.category
                    )
                    : ["No sales"];


            const salesValues =
                salesRows.length
                    ? salesRows.map(
                        (item) =>
                            item.value
                    )
                    : [1];


            /* =================================================
               VIEWS
            ================================================= */

            const viewRows =
                rows
                    .map(
                        (item) => ({

                            category:
                                item.category ||
                                "Uncategorised",

                            value:
                                Number(
                                    item.views ||
                                    0
                                )

                        })
                    )
                    .filter(
                        (item) =>

                            Number.isFinite(
                                item.value
                            ) &&

                            item.value > 0
                    );


            const viewLabels =
                viewRows.length
                    ? viewRows.map(
                        (item) =>
                            item.category
                    )
                    : ["No views"];


            const viewValues =
                viewRows.length
                    ? viewRows.map(
                        (item) =>
                            item.value
                    )
                    : [1];


            /* =================================================
               SALES DONUT
            ================================================= */

            const salesOptions =
                this.getDonutOptions(
                    salesLabels,
                    salesValues,
                    "Total Sales"
                );


            /* =================================================
               VIEWS DONUT
            ================================================= */

            const viewsOptions =
                this.getDonutOptions(
                    viewLabels,
                    viewValues,
                    "Total Views"
                );


            try {

                const sales =
                    new ApexCharts(
                        salesElement,
                        salesOptions
                    );


                const views =
                    new ApexCharts(
                        viewsElement,
                        viewsOptions
                    );


                sales.render();

                views.render();


                this.charts.categorySales =
                    sales;

                this.charts.categoryViews =
                    views;

            } catch (error) {

                console.error(
                    "Category chart error:",
                    error
                );

            }

        },


        /* =====================================================
           DATE
        ===================================================== */

        formatDate(value) {

            if (!value) {
                return "-";
            }


            const date =
                new Date(value);


            if (
                Number.isNaN(
                    date.getTime()
                )
            ) {

                return String(value);

            }


            return date.toLocaleString(
                "en-IN"
            );

        },


        /* =====================================================
           MODULE
        ===================================================== */

        openModule(item = null) {

            this.formType =
                "module";


            this.editingId =
                item?.module_id ||
                0;


            this.form = {

                ...item,

                course_id:
                    this.course.course_id

            };

        },


        /* =====================================================
           LECTURE
        ===================================================== */

        openLecture(
            item = null,
            moduleId = ""
        ) {

            this.formType =
                "lecture";


            this.editingId =
                item?.lession_id ||
                0;


            this.form = {

                ...item,

                course_id:
                    this.course.course_id,

                lession_module:
                    item?.lession_module ||
                    moduleId

            };

        },


        /* =====================================================
           VIDEO
        ===================================================== */

        openVideo(item = null) {

            this.formType =
                "video";


            this.editingId =
                item?.course_video_id ||
                0;


            this.form = {

                ...item,

                course_id:
                    this.course.course_id

            };

        },


        /* =====================================================
           SAVE
        ===================================================== */

        async saveForm() {

            let response;


            if (
                this.formType ===
                "module"
            ) {

                response =
                    this.editingId

                        ? await updateCourseModuleApi({

                            ...this.form,

                            module_id:
                                this.editingId

                        })

                        : await createCourseModuleApi(
                            this.form
                        );

            }


            if (
                this.formType ===
                "lecture"
            ) {

                response =
                    this.editingId

                        ? await updateCourseLectureApi({

                            ...this.form,

                            lession_id:
                                this.editingId

                        })

                        : await createCourseLectureApi(
                            this.form
                        );

            }


            if (
                this.formType ===
                "video"
            ) {

                response =
                    this.editingId

                        ? await updateCourseVideoApi({

                            ...this.form,

                            course_video_id:
                                this.editingId

                        })

                        : await createCourseVideoApi(
                            this.form
                        );

            }


            if (response?.status) {

                this.formType =
                    "";

                await this.loadDetails();

            } else {

                this.error =
                    response?.success ||
                    "Save failed.";

            }

        },


        /* =====================================================
           DELETE MODULE
        ===================================================== */

        removeModule(item) {

            this.openConfirm(
                "module",
                item.module_title,
                item.module_id
            );

        },


        /* =====================================================
           DELETE LECTURE
        ===================================================== */

        removeLecture(item) {

            this.openConfirm(
                "lecture",
                item.lession_title,
                item.lession_id
            );

        },


        /* =====================================================
           DELETE VIDEO
        ===================================================== */

        removeVideo(item) {

            this.openConfirm(
                "video",
                item.video_title,
                item.course_video_id
            );

        },


        /* =====================================================
           OPEN CONFIRM
        ===================================================== */

        openConfirm(
            type,
            label,
            id
        ) {

            this.confirmDialog = {

                open:
                    true,

                type:
                    type,

                label:
                    label,

                id:
                    id

            };

        },


        /* =====================================================
           CLOSE CONFIRM
        ===================================================== */

        closeConfirm() {

            this.confirmDialog.open =
                false;

        },


        /* =====================================================
           CONFIRM DELETE
        ===================================================== */

        async confirmDelete() {

            const dialog =
                this.confirmDialog;


            let response;


            if (
                dialog.type ===
                "module"
            ) {

                response =
                    await deleteCourseModuleApi(
                        dialog.id
                    );

            }


            if (
                dialog.type ===
                "lecture"
            ) {

                response =
                    await deleteCourseLectureApi(
                        dialog.id
                    );

            }


            if (
                dialog.type ===
                "video"
            ) {

                response =
                    await deleteCourseVideoApi(
                        dialog.id
                    );

            }


            this.closeConfirm();


            if (response?.status) {

                await this.loadDetails();

            } else {

                this.error =
                    response?.success ||
                    "Delete failed.";

            }

        }

    }

};
</script>


<style scoped>

.page {
    padding: 20px;
    color: #0f172a;
}


/* =====================================================
   HEADER
===================================================== */

.page-header {

    display: flex;

    justify-content:
        space-between;

    align-items:
        flex-start;

    gap:
        16px;

    margin-bottom:
        18px;

}


.page-header h2 {

    margin:
        8px 0 4px;

    font-size:
        24px;

}


.page-header p,
.back-btn,
small {

    color:
        #64748b;

    font-size:
        12px;

}


.back-btn {

    border:
        0;

    background:
        none;

    padding:
        0;

    cursor:
        pointer;

}


/* =====================================================
   CARDS
===================================================== */

.card,
.summary-card {

    background:
        #fff;

    border:
        1px solid #e2e8f0;

    border-radius:
        12px;

}


/* =====================================================
   SUMMARY
===================================================== */

.summary-grid {

    display:
        grid;

    grid-template-columns:
        repeat(4, 1fr);

    gap:
        12px;

    margin-bottom:
        18px;

}


.summary-card {

    padding:
        16px;

}


.summary-card span {

    display:
        block;

    color:
        #64748b;

    font-size:
        11px;

}


.summary-card strong {

    display:
        block;

    margin-top:
        7px;

    font-size:
        22px;

}


/* =====================================================
   TABS
===================================================== */

.tabs {

    display:
        flex;

    gap:
        4px;

    border-bottom:
        1px solid #e2e8f0;

    margin-bottom:
        16px;

}


.tabs button {

    border:
        0;

    border-bottom:
        2px solid transparent;

    background:
        none;

    padding:
        12px 14px;

    color:
        #64748b;

    cursor:
        pointer;

}


.tabs button.active {

    border-color:
        #2563eb;

    color:
        #2563eb;

    font-weight:
        700;

}


/* =====================================================
   REPORT
===================================================== */

.report-content {

    display:
        grid;

    gap:
        16px;

}


/* =====================================================
   IMPORTANT CHART LAYOUT

   70% MAIN CHART
   30% DONUT

   Row 1:
   Orders + Category Sales

   Row 2:
   Views + Category Views
===================================================== */

.chart-row {

    display:
        grid;

    grid-template-columns:
        minmax(0, 7fr)
        minmax(0, 3fr);

    gap:
        16px;

    width:
        100%;

}


.chart-card {

    min-width:
        0;

    overflow:
        hidden;

}


.main-chart-card {

    min-width:
        0;

}


.donut-chart-card {

    min-width:
        0;

}


.main-chart {

    width:
        100%;

    min-height:
        300px;

}


.donut-chart {

    width:
        100%;

    min-height:
        270px;

}


/* =====================================================
   SECTION HEADER
===================================================== */

.section-header {

    display:
        flex;

    justify-content:
        space-between;

    align-items:
        center;

    padding:
        16px;

}


.section-header h3 {

    margin:
        0;

    font-size:
        15px;

}


/* =====================================================
   TABLES
===================================================== */

.content-grid {

    display:
        grid;

    grid-template-columns:
        1fr 1fr;

    gap:
        16px;

}


.section-card,
.table-card {

    overflow:
        hidden;

}


.table-wrapper {

    overflow:
        auto;

}


.modern-table {

    width:
        100%;

    border-collapse:
        collapse;

}


.modern-table th,
.modern-table td {

    padding:
        12px 16px;

    border-bottom:
        1px solid #f1f5f9;

    text-align:
        left;

    white-space:
        nowrap;

    font-size:
        12px;

}


.modern-table th {

    background:
        #f8fafc;

    color:
        #64748b;

    text-transform:
        uppercase;

    font-size:
        10px;

}


.modern-table small {

    display:
        block;

    margin-top:
        3px;

}


.empty,
.loading-box {

    padding:
        28px;

    text-align:
        center;

    color:
        #64748b;

}


.badge {

    background:
        #ecfdf5;

    color:
        #047857;

    border-radius:
        12px;

    padding:
        4px 8px;

    font-size:
        10px;

}


/* =====================================================
   MODULES
===================================================== */

.module-block {

    border-top:
        1px solid #f1f5f9;

    padding:
        14px 16px;

}


.module-header,
.lecture-row {

    display:
        flex;

    align-items:
        center;

    gap:
        12px;

}


.module-header {

    justify-content:
        space-between;

}


.module-header strong {

    display:
        block;

}


.module-header small {

    display:
        block;

    margin-top:
        4px;

}


.lecture-row {

    padding:
        10px 0 10px 18px;

    border-bottom:
        1px solid #f8fafc;

}


.lecture-row span {

    flex:
        1;

}


.lecture-row small {

    margin-left:
        auto;

}


.link-btn {

    border:
        0;

    background:
        none;

    color:
        #2563eb;

    padding:
        10px 0 0 18px;

    cursor:
        pointer;

    font-size:
        12px;

}


/* =====================================================
   BUTTONS
===================================================== */

.btn-primary,
.btn-secondary,
.btn-danger {

    border-radius:
        8px;

    padding:
        9px 13px;

    cursor:
        pointer;

    font-size:
        12px;

}


.btn-primary {

    border:
        0;

    background:
        #2563eb;

    color:
        #fff;

}


.btn-secondary {

    border:
        1px solid #e2e8f0;

    background:
        #fff;

    color:
        #334155;

}


.btn-danger {

    border:
        0;

    background:
        #dc2626;

    color:
        #fff;

}


/* =====================================================
   ICON BUTTONS
===================================================== */

.icon-btn {

    border:
        0;

    border-radius:
        6px;

    width:
        28px;

    height:
        28px;

    margin-left:
        4px;

    cursor:
        pointer;

}


.icon-btn.edit {

    color:
        #2563eb;

    background:
        #eff6ff;

}


.icon-btn.delete {

    color:
        #dc2626;

    background:
        #fef2f2;

}


.url-cell {

    max-width:
        280px;

    overflow:
        hidden;

    text-overflow:
        ellipsis;

}


/* =====================================================
   MODAL
===================================================== */

.modal-overlay {

    position:
        fixed;

    inset:
        0;

    z-index:
        20;

    background:
        #0f172a66;

    display:
        flex;

    align-items:
        center;

    justify-content:
        center;

    padding:
        20px;

}


.modal-box {

    width:
        min(600px, 100%);

    background:
        #fff;

    border-radius:
        12px;

    padding:
        20px;

}


.confirm-box {

    max-width:
        380px;

    text-align:
        center;

}


.confirm-icon {

    display:
        grid;

    place-items:
        center;

    width:
        44px;

    height:
        44px;

    margin:
        0 auto 12px;

    border-radius:
        50%;

    color:
        #dc2626;

    background:
        #fef2f2;

    font-size:
        22px;

}


.modal-close {

    border:
        0;

    background:
        none;

    font-size:
        18px;

    cursor:
        pointer;

}


.form-grid {

    display:
        grid;

    grid-template-columns:
        1fr 1fr;

    gap:
        12px;

    padding:
        0 16px 16px;

}


.form-grid label {

    color:
        #475569;

    font-size:
        11px;

}


.form-grid input,
.form-grid textarea {

    display:
        block;

    width:
        100%;

    box-sizing:
        border-box;

    margin-top:
        5px;

    padding:
        9px;

    border:
        1px solid #e2e8f0;

    border-radius:
        7px;

}


.form-grid textarea {

    min-height:
        80px;

}


.form-grid .full {

    grid-column:
        1 / -1;

}


.modal-actions {

    display:
        flex;

    justify-content:
        flex-end;

    gap:
        8px;

    padding:
        0 16px;

}


/* =====================================================
   ERROR
===================================================== */

.error-box {

    padding:
        12px;

    margin-bottom:
        16px;

    color:
        #b91c1c;

    background:
        #fef2f2;

    border:
        1px solid #fecaca;

    border-radius:
        8px;

}


/* =====================================================
   RESPONSIVE
===================================================== */

@media (max-width: 900px) {

    .chart-row {

        grid-template-columns:
            minmax(0, 1fr);

    }

}


@media (max-width: 800px) {

    .summary-grid,
    .content-grid {

        grid-template-columns:
            1fr 1fr;

    }

}


@media (max-width: 560px) {

    .summary-grid,
    .content-grid,
    .chart-row,
    .form-grid {

        grid-template-columns:
            1fr;

    }


    .form-grid .full {

        grid-column:
            auto;

    }


    .tabs {

        overflow:
            auto;

    }


    .tabs button {

        white-space:
            nowrap;

    }

}

</style>